// optisel-frontend/src/pages/issues/BrowseIssuesPage.tsx
import React, {useContext} from 'react';
import {Card} from "../../components/Card";
import {createAsyncResultsProviderViaApiWithGetParams, SearchResultsOrder, useOneOfButtonFilterManager, useSearch, useTextFilterManager} from "../../platform/search/PlatformSearch";
import {AdditionalTextFilter, ButtonGroupFilter, CheckboxFilter, CurrentlyUsedFilters, OrderSelector, SearchQueryBar} from "../../components/search/MetronicSearchComponents";
import {useCheckboxFilterManager} from "../../components/search/useCheckboxFilterManager";
import {IssueApiJson} from "./IssueApiJson";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {NavLink} from "react-router-dom";
import {HotIssueContext} from "./hotssue/HotIssueContextProvider";
import {IssuePartyLines} from "./IssuePartyLines";
import {IssueAge} from "./IssueAge";
import {formatPolishDateTime} from "./IssueDateFormat";
import {Protected} from "../../layout/Protected";

const RESULTS_PER_PAGE = 20;

export function BrowseIssuesPage() {
    const queryFilterManager = useTextFilterManager("query", "Szukaj");
    const caseNumberFilterManager = useTextFilterManager("caseNumber", "Numer sprawy");
    const inquiryNumberFilterManager = useTextFilterManager("inquiryNumber", "Numer zapytania");
    const archivedFilterManager = useCheckboxFilterManager("archived", "Przeglądaj zarchiwizowane");
    const assigneeFilterManager = useOneOfButtonFilterManager(
        "assigneeFilter",
        "Przypisanie",
        [
            {label: "Wszystkie", value: "ALL"},
            {label: "Nieprzypisane", value: "UNASSIGNED"},
            {label: "Przypisane", value: "ASSIGNED"},
            {label: "Moje", value: "MINE"},
        ],
        "ALL"
    );

    const {makeHotIssue} = useContext(HotIssueContext);

    const availableOrders: SearchResultsOrder[] = [
        {id: "last-activity-desc", name: "Ostatnio aktywne", informationalDirection: "desc"},
        {id: "creation-desc", name: "Najnowsze", informationalDirection: "desc"}
    ];

    const {
        currentlyActiveFilterManagers,
        refresh,
        isLoading,
        results,
        reset,
        currentOrderId,
        changeOrderId,
        setCurrentPart,
        paginationDriver: {
            currentPage,
            switchToNextPage,
            nextPageSwitchAvailable,
        }
    } = useSearch<any, IssueApiJson>(
        createAsyncResultsProviderViaApiWithGetParams<IssueApiJson>("/api/issues/search"),
        [queryFilterManager, caseNumberFilterManager, inquiryNumberFilterManager, assigneeFilterManager, archivedFilterManager],
        availableOrders,
        RESULTS_PER_PAGE
    );

    const goToFirstPage = () => setCurrentPart({offset: 0, limit: RESULTS_PER_PAGE});
    const showFooter = currentPage > 1 || nextPageSwitchAvailable;
    const paginationFooter = showFooter && (
        <div className={"d-flex gap-3 align-items-center mt-3"}>
            {currentPage > 1 && <button className={"btn btn-outline btn-outline-dashed btn-sm"} onClick={goToFirstPage}>« Pierwsza strona</button>}
            <span className={"text-muted"}>Strona {currentPage}</span>
            {nextPageSwitchAvailable && <button className={"btn btn-outline btn-outline-dashed btn-sm"} onClick={switchToNextPage}>Szukaj więcej »</button>}
        </div>
    );

    return <>
        <Card additionalClassNames={"card mb-4"}>
            <div className="row">
                <div className="col-9 d-flex flex-column justify-content-end">
                    <SearchQueryBar queryFilterManager={queryFilterManager} reset={reset} refresh={refresh}/>
                </div>
                <div className="col-3">
                    <AdditionalTextFilter fanNameFilterManager={caseNumberFilterManager}/>
                </div>
            </div>
            <div className="separator separator-dashed mt-6 mb-4"/>
            <div className="row">
                <div className="col-2 d-flex flex-column justify-content-end">
                    <CheckboxFilter filterManager={archivedFilterManager}/>
                </div>
                <div className="col-2">
                    <AdditionalTextFilter fanNameFilterManager={inquiryNumberFilterManager}/>
                </div>
                <div className="col-6 d-flex flex-column justify-content-end">
                    <ButtonGroupFilter filterManager={assigneeFilterManager}/>
                </div>
                <div className="col-2 d-flex flex-column justify-content-end">
                    <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
                        <NavLink to={"/create-issue"} className={"btn btn-sm btn-primary"}><i className={"fas fa-plus-circle"}/> Utwórz sprawę</NavLink>
                    </Protected>
                </div>
            </div>
        </Card>
        <Card>
            <div className={"d-flex mb-2"}>
                <div className={"flex-grow-1"}>
                    <CurrentlyUsedFilters currentlyActiveFilterManagers={currentlyActiveFilterManagers}/>
                </div>
                <div className={"d-flex align-content-center"}>
                    <OrderSelector changeOrderId={changeOrderId} currentOrderId={currentOrderId} availableOrders={availableOrders}/>
                </div>
            </div>

            <div>
                {isLoading ? <div className="text-center mt-5 mb-5"><UboldSpinner/></div> : null}
            </div>
            {!isLoading && results.length === 0 && <div className="text-center fs-4 text-gray-600 mt-5 mb-5">Brak rezultatów</div>}
            {!isLoading && results.length === 0 && currentPage > 1 && paginationFooter}

            {!isLoading && results.length > 0 && <div className={"table-responsive"}>
                <table className={"table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3"}>
                    <thead>
                    <tr className="fw-bolder text-muted">
                        <th className="w-100px">Nazwa / Nr / wew. identyfikator</th>

                        <th className="min-w-120px">Referencje</th>
                        <th className="min-w-120px">Aktywność / Utworzono</th>
                        <th className="text-end">Akcje</th>
                    </tr>
                    </thead>
                    <tbody>
                    {results.map(result => <tr key={result.id}>
                        <td className="d-flex flex-column gap-2 align-items-start">
                            <div className="fw-boldest fs-5">{result.name}</div>
                            {result.issueReferences.wentechCaseNumber !== "" && <div className={"badge fs-5 badge-secondary p-2  text-wrap"}>{result.issueReferences.wentechCaseNumber}</div>}

                            <div className={"text-gray-200 text-nowrap"}>{result.id}</div>
                        </td>

                        <td className="align-top">
                            <div className={"d-flex flex-column gap-2"}>
                                {result.issueReferences.wentechInquiryNumber !== "" && <div className={"badge badge-warning p-2 text-wrap"}><span className="">Zapytanie (Wentech):</span> {result.issueReferences.wentechInquiryNumber}</div>}
                                {result.issueReferences.customerOrderNumber !== "" && <div className={"badge badge-light p-2  text-wrap"}><span className="text-gray-400">Zamówienie (klienta):</span> {result.issueReferences.customerOrderNumber}</div>}
                                {result.issueReferences.customerReferenceNumber !== "" && <div className={"badge badge-light p-2  text-wrap"}><span className="text-gray-400">Referencja (klienta):</span> {result.issueReferences.customerReferenceNumber}</div>}
                            </div>
                        </td>
                        <td className="d-flex flex-column gap-2 align-items-start">

                            <IssueAge creationInstant={result.creationInstant}/>
                            <div className={"fw-boldest fs-5"}>{formatPolishDateTime(result.lastActivity)}</div>
                            <div>{formatPolishDateTime(result.creationInstant)}</div>
                            <div className={"text-gray-400"}>przez: {result.originalCreatorName}</div>
                            <IssuePartyLines reporter={result.reporter} assignee={result.assignee}/>
                        </td>
                        <td className="align-top text-end">
                            <div>
                                <NavLink to={`/show-issue/${result.id}`} className={"btn btn-sm btn-light-primary"}>Zobacz</NavLink>
                            </div>
                            {result.active && <div>
                                <Protected privilege={"PERM_HISTORY_AND_ISSUES_MANAGEMENT"}>
                                    <button className={"btn btn-sm btn-primary"} onClick={() => makeHotIssue(result.id)}>Pracuj z</button>
                                </Protected>
                            </div>}
                        </td>
                    </tr>)}
                    </tbody>
                </table>

                {paginationFooter}
            </div>}
        </Card>
    </>;
}